import { memo, useMemo } from 'react';
import styled, { css } from 'styled-components';
import type { ITextProps, PredefinedTextVariants } from './Text.types';

export const typographyCss = ({
	variant,
	bold,
}: {
	variant?: PredefinedTextVariants;
	bold?: boolean;
}): ReturnType<typeof css> => {
	switch (variant) {
		case 'sans/58/800':
			return css`
				font-weight: 800;
				font-size: 3.625rem;
				letter-spacing: 0;
				line-height: 4.5rem;
			`;
		case 'sans/58/600':
			return css`
				font-weight: 600;
				font-size: 3.625rem;
				letter-spacing: 0;
				line-height: 4.5rem;
			`;
		case 'sans/58/400':
			return css`
				font-weight: normal;
				font-size: 3.625rem;
				letter-spacing: 0;
				line-height: 4.5rem;
			`;
		case 'sans/58/200':
			return css`
				font-weight: 200;
				font-size: 3.625rem;
				letter-spacing: 0;
				line-height: 4.5rem;
			`;
		case 'sans/50/800':
			return css`
				font-weight: 800;
				font-size: 3.125rem;
				letter-spacing: 0;
				line-height: 4rem;
			`;
		case 'sans/50/600':
			return css`
				font-weight: 600;
				font-size: 3.125rem;
				letter-spacing: 0;
				line-height: 4rem;
			`;
		case 'sans/50/400':
			return css`
				font-weight: normal;
				font-size: 3.125rem;
				letter-spacing: 0;
				line-height: 4rem;
			`;
		case 'sans/50/200':
			return css`
				font-weight: 200;
				font-size: 3.125rem;
				letter-spacing: 0;
				line-height: 4rem;
			`;
		case 'sans/36/800':
			return css`
				font-weight: 800;
				font-size: 2.25rem;
				letter-spacing: 0;
				line-height: 3rem;
			`;
		case 'sans/36/600':
			return css`
				font-weight: 600;
				font-size: 2.25rem;
				letter-spacing: 0;
				line-height: 3rem;
			`;
		case 'sans/36/400':
			return css`
				font-weight: normal;
				font-size: 2.25rem;
				letter-spacing: 0;
				line-height: 3rem;
			`;
		case 'sans/36/200':
			return css`
				font-weight: 200;
				font-size: 2.25rem;
				letter-spacing: 0;
				line-height: 3rem;
			`;
		case 'sans/30/800':
			return css`
				font-weight: 800;
				font-size: 1.875rem;
				letter-spacing: 0;
				line-height: 2.5rem;
			`;
		case 'sans/30/600':
			return css`
				font-weight: 600;
				font-size: 1.875rem;
				letter-spacing: 0;
				line-height: 2.5rem;
			`;
		case 'sans/30/400':
			return css`
				font-weight: normal;
				font-size: 1.875rem;
				letter-spacing: 0;
				line-height: 2.5rem;
			`;
		case 'sans/30/200':
			return css`
				font-weight: 200;
				font-size: 1.875rem;
				letter-spacing: 0;
				line-height: 2.5rem;
			`;
		case 'sans/25/800':
			return css`
				font-weight: 800;
				font-size: 1.563rem;
				letter-spacing: 0;
				line-height: 2rem;
			`;
		case 'sans/25/600':
			return css`
				font-weight: 600;
				font-size: 1.563rem;
				letter-spacing: 0;
				line-height: 2rem;
			`;
		case 'sans/25/400':
			return css`
				font-weight: normal;
				font-size: 1.563rem;
				letter-spacing: 0;
				line-height: 2rem;
			`;
		case 'sans/25/200':
			return css`
				font-weight: 200;
				font-size: 1.563rem;
				letter-spacing: 0;
				line-height: 2rem;
			`;
		case 'sans/24/600':
			return css`
				font-weight: 600;
				font-size: 1.5rem;
				letter-spacing: 0;
				line-height: 2rem;
			`;
		case 'sans/17/800':
			return css`
				font-weight: 800;
				font-size: 1.063rem;
				letter-spacing: 0;
				line-height: 1.5rem;
			`;
		case 'sans/17/600':
			return css`
				font-weight: 600;
				font-size: 1.063rem;
				letter-spacing: 0;
				line-height: 1.5rem;
			`;
		case 'sans/17/400':
			return css`
				font-weight: normal;
				font-size: 1.063rem;
				letter-spacing: 0;
				line-height: 1.5rem;
			`;
		case 'sans/17/200':
			return css`
				font-weight: 200;
				font-size: 1.063rem;
				letter-spacing: 0;
				line-height: 1.5rem;
			`;
		case 'sans/14/800':
			return css`
				font-weight: 800;
				font-size: 0.875rem;
				letter-spacing: 0;
				line-height: 1.25rem;
			`;
		case 'sans/14/600':
			return css`
				font-weight: 600;
				font-size: 0.875rem;
				letter-spacing: 0;
				line-height: 1.25rem;
			`;
		case 'sans/14/500':
			return css`
				font-weight: 500;
				font-size: 0.875rem;
				letter-spacing: 0;
				line-height: 1.25rem;
			`;
		case 'sans/14/400':
			return css`
				font-weight: normal;
				font-size: 0.875rem;
				letter-spacing: 0;
				line-height: 1.25rem;
			`;
		case 'sans/14/200':
			return css`
				font-weight: 200;
				font-size: 0.875rem;
				letter-spacing: 0;
				line-height: 1.25rem;
			`;
		case 'sans/11/800':
			return css`
				font-weight: 800;
				font-size: 0.688rem;
				letter-spacing: 0;
				line-height: 1rem;
			`;
		case 'sans/11/600':
			return css`
				font-weight: 600;
				font-size: 0.688rem;
				letter-spacing: 0;
				line-height: 1rem;
			`;
		case 'sans/12/600':
			return css`
				font-weight: 600;
				font-size: 0.75rem;
				letter-spacing: 0;
				line-height: 1rem;
			`;
		case 'sans/13/600':
			return css`
				font-weight: 600;
				font-size: 0.813rem;
				letter-spacing: 0;
				line-height: 1.125rem;
			`;
		case 'sans/13/500':
			return css`
				font-weight: 500;
				font-size: 0.813rem;
				letter-spacing: 0;
				line-height: 1.125rem;
			`;
		case 'sans/13/400':
			return css`
				font-weight: normal;
				font-size: 0.813rem;
				letter-spacing: 0;
				line-height: 1.125rem;
			`;
		case 'sans/12/500':
			return css`
				font-weight: 500;
				font-size: 0.75rem;
				letter-spacing: 0;
				line-height: 1rem;
			`;
		case 'sans/10/400':
			return css`
				font-weight: normal;
				font-size: 0.625rem;
				letter-spacing: 0;
				line-height: 0.875rem;
			`;
		case 'sans/12/400':
			return css`
				font-weight: normal;
				font-size: 0.75rem;
				letter-spacing: 0;
				line-height: 1rem;
			`;
		case 'sans/11/400':
			return css`
				font-weight: normal;
				font-size: 0.688rem;
				letter-spacing: 0;
				line-height: 1rem;
			`;
		case 'sans/11/200':
			return css`
				font-weight: 200;
				font-size: 0.688rem;
				letter-spacing: 0;
				line-height: 1rem;
			`;
		case 'sans/09/800':
			return css`
				font-weight: 800;
				font-size: 0.563rem;
				letter-spacing: 0;
				line-height: 0.75rem;
			`;
		case 'sans/09/600':
			return css`
				font-weight: 600;
				font-size: 0.563rem;
				letter-spacing: 0;
				line-height: 0.75rem;
			`;
		case 'sans/09/400':
			return css`
				font-weight: normal;
				font-size: 0.563rem;
				letter-spacing: 0;
				line-height: 0.75rem;
			`;
		case 'sans/09/200':
			return css`
				font-weight: 200;
				font-size: 0.563rem;
				letter-spacing: 0;
				line-height: 0.75rem;
			`;
		case 'sans/05/800':
			return css`
				font-weight: 800;
				font-size: 0.312rem;
				letter-spacing: 0;
				line-height: 0.1rem;
			`;
		case 'h1':
			return css`
				font-size: 3rem;
				line-height: 110%;
			`;
		case 'h2':
			return css`
				font-size: 2.5rem;
				line-height: 110%;
			`;
		case 'h3':
			return css`
				font-size: 2rem;
				line-height: 120%;
				letter-spacing: 0.02rem;
			`;
		case 'h4':
			return css`
				font-size: 1.5rem;
				line-height: 130%;
				letter-spacing: 0.04em;
			`;
		case 'h5':
			return css`
				font-size: 1.125rem;
				line-height: 140%;
				letter-spacing: 0.04rem;
			`;
		case 'p-lg':
			return css`
				font-size: 1rem;
				line-height: 150%;
			`;
		case 'p-md':
			return css`
				font-size: 0.875rem;
				line-height: 150%;
			`;
		case 'p-sm':
			return css`
				font-size: 0.75rem;
				line-height: 150%;
			`;
		case 'p-mc':
			return css`
				font-size: 0.625rem;
				line-height: 120%;
			`;
		case 'p-mm':
			return css`
				font-size: 0.5rem;
				line-height: 120%;
				letter-spacing: 0.1em;
			`;
		case 'button':
			return css`
				font-size: 0.875rem;
				line-height: 120%;
				letter-spacing: 0.04rem;

				${bold ? `font-weight: 600;` : ``}
			`;
		case 'button-small':
			return css`
				font-size: 0.75rem;
				line-height: 110%;
				letter-spacing: 0.04rem;

				${bold ? `font-weight: 600;` : ``}
			`;
		case 'overline':
			return css`
				font-size: 0.625rem;
				line-height: 100%;
				letter-spacing: 0.5px;

				${bold ? `font-weight: 600;` : ``}
			`;
		case 'miniOverline':
			return css`
				font-size: 0.5rem;
				line-height: 100%;
				letter-spacing: 0.5px;
			`;
		case 'caption':
			return css`
				font-size: 0.75rem;
				line-height: 150%;
				letter-spacing: 0.5px;

				${bold ? `font-weight: 500;` : ``}
			`;
		default:
			return css``;
	}
};

const Text = ({
	variant,
	className,
	children,
	i18NextInline,
	nonselectable,
	noWrap,
	multiLineElipsis,
	elipsis,
	lowercase,
	uppercase,
	capitalize,
	center,
	capitalizeFirst,
	colorFaded,
	colorError,
	colorLink,
	isLink,
	bold,
	semiBold,
	disabled,
	disableVariantMapping,
	onClick,
	alwaysWrap,
	'data-testid': dataTestId, // Destructure and rename here
	'data-tooltip-id': dataTooltipId,
}: ITextProps) => {
	const newVariantMapping: ITextProps['variant'] = useMemo(() => {
		if (!variant) {
			return;
		}
		if (disableVariantMapping) {
			return variant;
		}
		if (variant === 'h1' && bold) {
			return 'sans/50/800';
		}
		if (variant === 'h1' && !bold) {
			return 'sans/50/400';
		}
		if (variant === 'h2' && bold) {
			return 'sans/36/800';
		}
		if (variant === 'h2' && !bold) {
			return 'sans/36/400';
		}
		if (variant === 'h3' && bold) {
			return 'sans/30/800';
		}
		if (variant === 'h3' && semiBold && !bold) {
			return 'sans/30/600';
		}
		if (variant === 'h3' && !bold) {
			return 'sans/30/400';
		}
		if (variant === 'h4' && semiBold && !bold) {
			return 'sans/25/600';
		}
		if (variant === 'h4' && bold) {
			return 'sans/25/800';
		}
		if (variant === 'h4' && !bold) {
			return 'sans/25/400';
		}
		if (variant === 'h5' && bold) {
			return 'sans/17/800';
		}
		if (variant === 'h5' && !bold) {
			return 'sans/17/400';
		}
		if (variant === 'button' && bold) {
			return 'sans/14/800';
		}
		if (variant === 'button' && !bold) {
			return 'sans/14/400';
		}
		if (variant === 'button-small' && !bold) {
			return 'sans/14/400';
		}
		if (variant === 'button-small' && bold) {
			return 'sans/14/800';
		}
		if (variant === 'p-lg' && bold) {
			return 'sans/17/800';
		}
		if (variant === 'p-lg' && !bold) {
			return 'sans/17/400';
		}
		if (variant === 'p-md' && bold) {
			return 'sans/14/800';
		}
		if (variant === 'p-md' && !bold) {
			return 'sans/14/400';
		}
		if (variant === 'p-sm' && semiBold) {
			return 'sans/14/600';
		}
		if (variant === 'p-sm' && bold) {
			return 'sans/14/800';
		}
		if (variant === 'p-sm' && !bold) {
			return 'sans/14/400';
		}
		if (variant === 'p-mc' && bold) {
			return 'sans/11/800';
		}
		if (variant === 'p-mc' && !bold) {
			return 'sans/11/400';
		}
		if (variant === 'p-mm' && bold) {
			return 'sans/09/800';
		}
		if (variant === 'p-mm' && !bold) {
			return 'sans/09/400';
		}
		if (variant === 'caption' && bold) {
			return 'sans/14/800';
		}
		if (variant === 'caption' && !bold) {
			return 'sans/14/400';
		}
		if (variant === 'overline' && bold) {
			return 'sans/09/800';
		}
		if (variant === 'overline' && !bold) {
			return 'sans/14/400';
		}
		if (variant === 'miniOverline' && bold) {
			return 'sans/09/800';
		}
		if (variant === 'miniOverline' && !bold) {
			return 'sans/09/400';
		}
		return 'sans/50/800';
	}, [variant, bold, semiBold, disableVariantMapping]);
	return (
		<TextVariant
			className={className}
			disabled={disabled}
			colorError={colorError}
			center={center}
			bold={bold}
			semiBold={semiBold}
			capitalize={capitalize}
			noWrap={noWrap}
			lowercase={lowercase}
			uppercase={uppercase}
			capitalizeFirst={capitalizeFirst}
			multiLineElipsis={multiLineElipsis}
			elipsis={elipsis}
			i18NextInline={i18NextInline}
			colorFaded={colorFaded}
			colorLink={colorLink}
			nonselectable={nonselectable}
			variant={newVariantMapping}
			onClick={onClick}
			isLink={isLink}
			alwaysWrap={alwaysWrap}
			data-testid={dataTestId}
			data-tooltip-id={dataTooltipId}
		>
			{children}
		</TextVariant>
	);
};

// Custom style props consumed only by the CSS below — they must NOT reach the
// DOM <span> (React warns about unknown/boolean attributes like `elipsis`).
// styled-components still passes them to the template interpolations; this only
// filters what is forwarded to the underlying element.
const TEXT_STYLE_PROPS = new Set([
	'disabled',
	'colorError',
	'center',
	'bold',
	'semiBold',
	'capitalize',
	'noWrap',
	'lowercase',
	'uppercase',
	'capitalizeFirst',
	'multiLineElipsis',
	'elipsis',
	'i18NextInline',
	'colorFaded',
	'colorLink',
	'nonselectable',
	'isLink',
	'alwaysWrap',
	'variant',
]);

const TextVariant = styled.span.withConfig({
	shouldForwardProp: (prop) => !TEXT_STYLE_PROPS.has(prop),
})<ITextProps>`
	font-weight: 400;
	display: inline-block;
	white-space: pre-line;

	${({ variant, bold }) => typographyCss({ variant, bold })}

	${({ nonselectable }) => (nonselectable ? 'user-select: none;' : ``)}

	${({ i18NextInline }) =>
		i18NextInline &&
		`
        display: contents;
    `}

	${({ multiLineElipsis }) =>
		multiLineElipsis?.enabled &&
		`
        display: -webkit-box;
        -webkit-line-clamp: ${multiLineElipsis.numberOfLines};
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-break: break-all;
    `}

	${({ alwaysWrap }) => alwaysWrap && `overflow-wrap: anywhere;`}

	${({ elipsis }) =>
		elipsis &&
		`
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.4;
        display: block;
    `}

	${({ uppercase }) =>
		uppercase &&
		`
        text-transform: uppercase;
    `}
    ${({ lowercase }) =>
		lowercase &&
		`
        text-transform: lowercase;
    `}
	${({ noWrap }) =>
		noWrap &&
		`
        white-space: nowrap;
    `}
	${({ capitalize }) =>
		capitalize &&
		`
        text-transform: lowercase;
        &:first-line {
            text-transform: capitalize;
        }
    `}
	${({ capitalizeFirst }) =>
		capitalizeFirst &&
		`
        text-transform: lowercase;
        &:first-letter {
        text-transform: capitalize;
        }
    `}

	${({ center }) =>
		center &&
		`
        text-align: center;
    `}

	${({ bold }) => (bold ? `font-weight: 700;` : null)}


	${({ theme, colorLink }) =>
		colorLink &&
		`
            color: ${theme.v2.color.secondary[800]};
    `}

	${({ theme, isLink }) =>
		isLink &&
		css`
			color: ${theme.v2.color.secondary[800]};
			cursor: pointer;

			&:hover {
				text-decoration: underline;
			}
		`};

	${({ theme, colorFaded }) =>
		colorFaded &&
		`
            color: ${theme.v2.color.neutral[600]};
    `}

	${({ theme, colorError }) =>
		colorError &&
		`
              color: ${theme.v2.color.error[1000]};
      `}

	${({ disabled, theme }) =>
		disabled &&
		`
                color: ${theme.v2.color.neutral[500]};
        `}
`;

const Memoized = memo(Text);
const Styled = styled(Memoized)``;
export const RightAlignedTextWrapper = styled.div<{
	isNegative?: boolean;
}>`
	text-align: right;

	${Styled} {
		display: block;
	}

	${({ isNegative }) => `margin-right: ${isNegative ? '2.7rem' : '3rem'};`};
`;

export { Styled as Text };
